import React, { useState, useEffect } from 'react';
import { Modal, Checkbox, Button, Space, message, Spin, Alert, Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import * as accountService from '../../services/account/accountService';
import * as roleService from '../../services/role/roleService';

// 角色层级（从高到低）：用于确定用户拥有的最高等级角色
const ROLE_HIERARCHY = ['R-SUPER-ADMIN', 'ADMIN', 'MANAGER', 'PICKER'];
// 各最高角色对应的可授权角色范围
const ASSIGNABLE_ROLES_MAP = {
  'R-SUPER-ADMIN': ['ADMIN', 'MANAGER', 'PICKER'],
  ADMIN: ['MANAGER', 'PICKER'],
  MANAGER: ['MANAGER', 'PICKER'],
  PICKER: ['PICKER'],
};
// 角色编码到展示名称的映射（用于当前用户未拥有但在可授权范围内的角色）
const ROLE_NAME_MAP = {
  'R-SUPER-ADMIN': 'Super Admin',
  ADMIN: 'Admin',
  MANAGER: 'Manager',
  PICKER: 'Picker',
};

/**
 * Account Role Authorization Modal Component
 * @author AI Assistant
 * @description Modal component for managing account role assignments with tree selection
 */
const AccountRoleAuthModal = ({ visible, account, onCancel, onSuccess }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [roleList, setRoleList] = useState([]);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [originalCheckedKeys, setOriginalCheckedKeys] = useState([]);

  // Load role data and account permissions when modal opens
  useEffect(() => {
    if (visible && account) {
      loadData();
    }
  }, [visible, account]);

  // Combined load function to ensure proper sequencing
  const loadData = async () => {
    await loadRoleData();
    await loadAccountRoles();
  };

  // Reset state when modal closes
  useEffect(() => {
    if (!visible) {
      resetState();
    }
  }, [visible]);

  const resetState = () => {
    setRoleList([]);
    setCheckedKeys([]);
    setOriginalCheckedKeys([]);
    setLoading(false);
    setSaving(false);
  };

  const loadRoleData = async () => {
    setLoading(true);
    try {
      const response = await roleService.queryCurrentAuthedRoles();
      if (response.success && response.data) {
        const authedRoles = response.data || [];
        const authedCodes = authedRoles.map(r => r.code);
        // 从高到低查找当前用户拥有的最高等级角色
        const highestRole = ROLE_HIERARCHY.find(code => authedCodes.includes(code));
        // 根据最高角色查得可授权范围
        const assignableCodes = highestRole ? (ASSIGNABLE_ROLES_MAP[highestRole] || []) : [];
        // 优先复用接口返回的角色对象，缺失的用名称映射兜底
        const roleMap = new Map(authedRoles.map(r => [r.code, r]));
        const displayRoles = assignableCodes.map(code =>
          roleMap.get(code) || { code, name: ROLE_NAME_MAP[code], type: 'ROLE' }
        );
        setRoleList(displayRoles);
      } else {
        message.error(response.errorMessage || t('account.loadRolesFailed'));
      }
    } catch (error) {
      console.error('加载角色数据失败:', error);
      message.error(t('account.loadRolesFailedRetry'));
    } finally {
      setLoading(false);
    }
  };

  const loadAccountRoles = async () => {
    if (!account?.loginId) return;
    
    console.log('开始加载账号角色:', account.loginId);
    
    try {
      const response = await roleService.pageRoles({
        type: "ROLE",
        authQueryType: "1", 
        authedAccount: account.loginId,
        currentPage: 1,
        pageSize: 1000
      });
      console.log('账号角色响应:', response);
      
      if (response.success && response.data && response.data.tableData) {
        // 从 tableData 中提取角色代码
        const roleCodes = response.data.tableData.map(role => role.code);
        console.log('获取到的角色代码:', roleCodes);
        setCheckedKeys(roleCodes);
        setOriginalCheckedKeys([...roleCodes]); // 保存原始状态用于比较
      } else {
        console.warn('获取账号角色失败:', response.errorMessage);
        // 如果获取失败，使用账号数据中的角色信息作为后备
        const roleCodes = account.roleList ? account.roleList.map(r => r.code) : [];
        console.log('使用后备数据:', roleCodes);
        setCheckedKeys(roleCodes);
        setOriginalCheckedKeys([...roleCodes]);
      }
    } catch (error) {
      console.error('加载账号角色失败:', error);
      // 使用账号数据中的角色信息作为后备
      const roleCodes = account.roleList ? account.roleList.map(r => r.code) : [];
      console.log('使用后备数据 (异常):', roleCodes);
      setCheckedKeys(roleCodes);
      setOriginalCheckedKeys([...roleCodes]);
    }
  };

  // Checkbox change handler (multiple role selection)
  const onCheck = (checkedValues) => {
    setCheckedKeys(checkedValues || []);
  };

  // Clear selection
  const clearAll = () => {
    setCheckedKeys([]);
  };

  // Check if there are changes
  const hasChanges = () => {
    return JSON.stringify(checkedKeys.sort()) !== JSON.stringify(originalCheckedKeys.sort());
  };

  // Save role assignments
  const handleSave = async () => {
    if (!account?.loginId) {
      console.error('Account info incomplete:', account);
      message.error(t('account.accountInfoIncomplete'));
      return;
    }
    
    if (!hasChanges()) {
      message.info(t('account.noChangesToSave'));
      return;
    }
    
    // Calculate added and removed roles
    const originalSet = new Set(originalCheckedKeys);
    const currentSet = new Set(checkedKeys);
    
    // Find added roles (selected but not in original list)
    const addedRoles = checkedKeys.filter(code => !originalSet.has(code));
    
    // Find removed roles (in original list but not selected)
    const removedRoles = originalCheckedKeys.filter(code => !currentSet.has(code));
    
    console.log('Role changes:', {
      loginId: account.loginId,
      addedRoles: addedRoles,
      removedRoles: removedRoles,
      originalRoleCodes: originalCheckedKeys,
      currentRoleCodes: checkedKeys
    });
    
    setSaving(true);
    try {
      // Process added roles
      if (addedRoles.length > 0) {
        const addResponse = await accountService.assignRoles({
          loginId: account.loginId,
          roleCodeSet: addedRoles
        });
        
        if (!addResponse || !addResponse.success) {
          console.error('Assign roles failed:', addResponse);
          message.error(addResponse?.errorMessage || t('account.assignRolesFailed'));
          return;
        }
      }
      
      // Process removed roles
      if (removedRoles.length > 0) {
        const removeResponse = await accountService.removeRoles({
          loginId: account.loginId,
          removeRoleCodeSet: removedRoles
        });
        
        if (!removeResponse || !removeResponse.success) {
          console.error('Remove roles failed:', removeResponse);
          message.error(removeResponse?.errorMessage || t('account.removeRolesFailed'));
          return;
        }
      }
      
      message.success(t('account.assignRolesSuccess'));
      onSuccess && onSuccess();
      onCancel();
    } catch (error) {
      console.error('Save role assignments failed:', error);
      message.error(error.message || t('account.assignRolesFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges()) {
      Modal.confirm({
        title: t('account.unsavedChangesTitle'),
        content: t('account.unsavedChangesContent'),
        okText: t('common.confirm'),
        cancelText: t('common.cancel'),
        onOk: () => {
          resetState();
          onCancel();
        }
      });
    } else {
      resetState();
      onCancel();
    }
  };

  return (
    <Modal
      title={t('account.roleAuthorizationTitle', { accountName: account?.name || account?.loginId || '' })}
      open={visible}
      onCancel={handleCancel}
      width={800}
      destroyOnHidden
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          {t('common.cancel')}
        </Button>,
        <Button 
          key="save" 
          type="primary" 
          loading={saving}
          onClick={handleSave}
          disabled={loading || !hasChanges()}
        >
          {t('common.save')}
        </Button>
      ]}
    >
      {/* 操作按钮区域 */}
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Button 
            size="small" 
            onClick={clearAll}
            disabled={loading || saving}
          >
            {t('account.clearAll')}
          </Button>
        </Space>
      </div>

      {/* 权限变更提示 */}
      {hasChanges() && (
        <Alert
          message={t('account.rolesChanged')}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          closable
        />
      )}
      
      {/* 角色选择区域 */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" tip={t('account.loadingRoles')} />
        </div>
      ) : (
        <div style={{ 
          border: '1px solid #d9d9d9', 
          borderRadius: '6px', 
          padding: '16px',
          maxHeight: '400px',
          overflowY: 'auto',
          backgroundColor: '#fafafa'
        }}>
          {roleList.length > 0 ? (
            <>
              <Checkbox.Group
                value={checkedKeys}
                onChange={onCheck}
                disabled={loading || saving}
                style={{ width: '100%' }}
              >
                <Row gutter={[16, 16]}>
                  {roleList.map(role => (
                    <Col span={8} key={role.code}>
                      <Checkbox 
                        value={role.code}
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          padding: '8px 12px',
                          border: '1px solid #e8e8e8',
                          borderRadius: '4px',
                          backgroundColor: '#fff',
                          marginBottom: '0'
                        }}
                      >
                        <span style={{ marginLeft: '8px', fontSize: '14px' }}>
                          {role.name}
                        </span>
                      </Checkbox>
                    </Col>
                  ))}
                </Row>
              </Checkbox.Group>
              
              {/* 统计信息 */}
              <div style={{ 
                marginTop: '16px', 
                padding: '8px 12px', 
                backgroundColor: '#e6f7ff', 
                borderRadius: '4px',
                fontSize: '12px',
                color: '#0050b3'
              }}>
                <div>{t('account.selectedRoleCount', { count: checkedKeys.length })}</div>
                <div>{t('account.totalRoleCount', { count: roleList.length })}</div>
              </div>
            </>
          ) : (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px 0',
              color: '#8c8c8c'
            }}>
              {t('account.noRolesAvailable')}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default AccountRoleAuthModal;